// --- Dependencies -------------------------------------------------------------------------------
import { Box, Checkbox, Chip, FormControl, InputLabel, ListItemText, MenuItem, OutlinedInput, Select } from "@mui/material";
// --- Our Modules --------------------------------------------------------------------------------
import useLang from "@/hooks/useLang";
import useTranslation from "@/hooks/useTranslation";
import { getTextFieldSx } from "@/utils";
import "./styles.css";

// --- Types --------------------------------------------------------------------------------------

export type TyBranchOption = { _id: string; name: string };

type Props = {
    value: "all" | string[];
    onChange: (value: "all" | string[]) => void;
    options: TyBranchOption[];
    disabled?: boolean;
};

// --- Logic --------------------------------------------------------------------------------------

export default function BranchAccessInput({ value, onChange, options, disabled }: Props) {
    const t = useTranslation();
    const lang = useLang((state) => state.lang);
    const textFieldSx = getTextFieldSx(lang === "ar" ? "rtl" : "ltr");

    const isAll = value === "all";

    return (
        <div className="branch-access-input">
            <div className="branch-access-input-toggle">
                <button
                    type="button"
                    className={`branch-access-input-btn${isAll ? " active" : ""}`}
                    onClick={() => onChange("all")}
                    disabled={disabled}
                >
                    {t.formClient.branchesAll}
                </button>
                <button type="button" className={`branch-access-input-btn${isAll ? "" : " active"}`} onClick={() => onChange([])} disabled={disabled}>
                    {t.formClient.branchesSome}
                </button>
            </div>

            {!isAll && (
                <FormControl fullWidth disabled={disabled} sx={textFieldSx}>
                    <InputLabel>{t.formClient.branchesSome}</InputLabel>
                    <Select
                        multiple
                        value={value}
                        onChange={(e) => onChange(e.target.value as string[])}
                        input={<OutlinedInput label={t.formClient.branchesSome} />}
                        renderValue={(selected) => (
                            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                                {(selected as string[]).map((id) => {
                                    const branch = options.find((b) => b._id === id);
                                    return branch ? (
                                        <Chip
                                            key={id}
                                            label={branch.name}
                                            size="small"
                                            sx={{
                                                backgroundColor: "var(--color-shaded-brand)",
                                                color: "var(--color-dark)",
                                                border: "1px solid var(--color-mid-brand)",
                                                height: "24px",
                                                fontFamily: "Alexandria, sans-serif",
                                                "& .MuiChip-label": { fontSize: "12px", fontWeight: 600, padding: "0 8px" },
                                            }}
                                        />
                                    ) : null;
                                })}
                            </Box>
                        )}
                        MenuProps={{ slotProps: { paper: { style: { maxHeight: 48 * 4.5 + 8 } } } }}
                    >
                        {options.map((branch) => (
                            <MenuItem key={branch._id} value={branch._id}>
                                <Checkbox
                                    checked={(value as string[]).includes(branch._id)}
                                    sx={{ color: "var(--color-gray-01)", "&.Mui-checked": { color: "var(--color-brand)" } }}
                                />
                                <ListItemText
                                    primary={branch.name}
                                    sx={{ "& .MuiTypography-root": { fontFamily: "Alexandria, sans-serif", fontSize: "13.5px" } }}
                                />
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            )}
        </div>
    );
}
